<template>
  <div class="contents">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row">
        <div class="col-lg-12">
          <div class="breadcrumb-main user-member justify-content-sm-between">
            <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
              <h4 class="text-capitalize fw-500 breadcrumb-title">Log Email Welcome</h4>
            </div>
            <div class="action-btn">
              <button @click="loadAll" class="btn btn-secondary text-white" style="margin: 5px">
                <i class="fas fa-sync"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Info Alert -->
      <div class="alert alert-info">
        <b>Cara kerja:</b>
        Email welcome dikirim otomatis (async) saat user verify OTP berhasil di endpoint <code>/auth/verify</code>.
        Email berisi kredensial login (email, username, password) + detail paket.
        Anti-duplicate: 1 user hanya dapat 1 email welcome.
      </div>

      <!-- Stats Cards -->
      <div class="row" v-if="stats">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #1976d2;">
            <div class="text-muted small">TOTAL TERKIRIM</div>
            <div class="h3 mb-0 font-weight-bold text-primary">{{ stats.total_success }}</div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #e53935;">
            <div class="text-muted small">TOTAL GAGAL</div>
            <div class="h3 mb-0 font-weight-bold text-danger">{{ stats.total_failed }}</div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #6c757d;">
            <div class="text-muted small">TOTAL LOG</div>
            <div class="h3 mb-0 font-weight-bold">{{ stats.total }}</div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card p-3" style="border-left: 4px solid #28a745;">
            <div class="text-muted small">SUCCESS RATE</div>
            <div class="h3 mb-0 font-weight-bold text-success">{{ successRate }}%</div>
          </div>
        </div>
      </div>

      <!-- Filter -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card p-3 mb-3">
            <div class="row">
              <div class="col-md-4 mb-2">
                <label class="small mb-1">Status</label>
                <select v-model="filter.success" @change="loadLogs(1)" class="form-control form-control-sm">
                  <option value="">Semua</option>
                  <option value="1">Sukses</option>
                  <option value="0">Gagal</option>
                </select>
              </div>
              <div class="col-md-5 mb-2">
                <label class="small mb-1">Email</label>
                <input v-model="filter.email" @input="onEmailFilter" type="text" class="form-control form-control-sm" placeholder="Cari email user...">
              </div>
              <div class="col-md-3 mb-2">
                <label class="small mb-1">Per Page</label>
                <select v-model.number="filter.limit" @change="loadLogs(1)" class="form-control form-control-sm">
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="userDatatable global-shadow border bg-white radius-xl w-100 mb-30">
            <div v-if="isLoading" class="text-center p-4">
              <div class="spinner-border" role="status"></div>
              <p class="mt-2 text-muted">Loading...</p>
            </div>
            <div v-else-if="logs.length === 0" class="text-center text-muted p-4">
              <p>Tidak ada log welcome email.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="bg-light">
                  <tr>
                    <th>Sent At</th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Username</th>
                    <th>Paket</th>
                    <th>Status</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in logs" :key="log.id">
                    <td><small>{{ formatTime(log.sent_at) }}</small></td>
                    <td>
                      <div class="font-weight-bold">{{ log.user_name }}</div>
                      <small class="text-muted">ID: {{ log.user_id }}</small>
                    </td>
                    <td><small style="font-family: monospace;">{{ log.email_to }}</small></td>
                    <td><small style="font-family: monospace;">{{ log.username }}</small></td>
                    <td><small>{{ log.package_name }}</small></td>
                    <td>
                      <span v-if="log.success === 1" class="badge badge-success">✓ Sent</span>
                      <span v-else class="badge badge-danger">✗ Failed</span>
                    </td>
                    <td>
                      <small v-if="log.error_message" class="text-danger" :title="log.error_message">
                        {{ log.error_message.substring(0, 40) }}{{ log.error_message.length > 40 ? '...' : '' }}
                      </small>
                      <small v-else class="text-muted">-</small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="!isLoading && logs.length > 0" class="d-flex justify-content-between align-items-center p-3">
              <small class="text-muted">
                Menampilkan {{ ((page - 1) * filter.limit) + 1 }} - {{ Math.min(page * filter.limit, total) }} dari {{ total }} log
              </small>
              <div class="btn-group">
                <button class="btn btn-sm btn-outline-primary" @click="loadLogs(page - 1)" :disabled="page <= 1">
                  ← Prev
                </button>
                <button class="btn btn-sm btn-outline-primary disabled">
                  Page {{ page }} / {{ totalPages }}
                </button>
                <button class="btn btn-sm btn-outline-primary" @click="loadLogs(page + 1)" :disabled="page >= totalPages">
                  Next →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      logs: [],
      stats: null,
      total: 0,
      totalPages: 1,
      page: 1,
      filter: {
        success: "",
        email: "",
        limit: 50,
      },
      isLoading: false,
      emailDebounce: null,
    };
  },
  computed: {
    successRate() {
      if (!this.stats || this.stats.total === 0) return 0;
      return Math.round((this.stats.total_success / this.stats.total) * 100);
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    formatTime(millis) {
      if (!millis) return "-";
      return new Date(parseInt(millis)).toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
    onEmailFilter() {
      clearTimeout(this.emailDebounce);
      this.emailDebounce = setTimeout(() => this.loadLogs(1), 500);
    },
    async loadAll() {
      await Promise.all([this.loadStats(), this.loadLogs(1)]);
    },
    async loadStats() {
      try {
        const resp = await axios.get("admin/welcome-emails/stats");
        this.stats = resp.data.data;
      } catch (err) {
        console.error("Failed loading stats:", err);
      }
    },
    async loadLogs(page) {
      this.isLoading = true;
      this.page = page;
      try {
        const params = {
          page: page,
          limit: this.filter.limit,
        };
        if (this.filter.success !== "") params.success = this.filter.success;
        if (this.filter.email) params.email = this.filter.email;

        const resp = await axios.get("admin/welcome-emails", { params });
        this.logs = resp.data.data.logs || [];
        this.total = resp.data.data.total || 0;
        this.totalPages = resp.data.data.total_pages || 1;
      } catch (err) {
        this.$toast.error("Gagal load log: " + (err.response?.data?.message || err.message));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
